


import React, { useState, useRef, useEffect, useMemo } from 'react';
import { CheckCircle, AlertTriangle, Share2, Timer, Trash2, ChevronDown, Crown, Clock, Moon, ShieldAlert, Wrench, Settings, X, LucideProps } from 'lucide-react';
import TagInput from '../../atoms/TagInput';
import ScheduleSelectionModal from '../ScheduleSelectionModal';
import { GeneralTabProps } from './types';
import { useScheduleGroups } from '../../../contexts/ScheduleGroupContext';

// Color and icon options for the edit modal
const COLOR_OPTIONS = ['slate', 'red', 'purple', 'amber', 'indigo', 'orange', 'blue', 'emerald', 'teal', 'pink'] as const;
const ICON_OPTIONS = [
  { name: '', label: 'None' },
  { name: 'AlertTriangle', label: 'Alert' },
  { name: 'Crown', label: 'Crown' },
  { name: 'Clock', label: 'Clock' },
  { name: 'Moon', label: 'Moon' },
  { name: 'ShieldAlert', label: 'Shield' },
  { name: 'Wrench', label: 'Wrench' },
] as const;

// Color dot map for combobox items
const DOT_COLORS: Record<string, string> = {
  slate: 'bg-slate-400',
  red: 'bg-red-500',
  purple: 'bg-purple-500',
  amber: 'bg-amber-500',
  indigo: 'bg-indigo-500',
  orange: 'bg-orange-500',
  blue: 'bg-blue-500',
  emerald: 'bg-emerald-500',
  teal: 'bg-teal-500',
  pink: 'bg-pink-500',
};

const ICON_MAP: Record<string, React.FC<LucideProps>> = {
  AlertTriangle,
  Crown,
  Clock,
  Moon,
  ShieldAlert,
  Wrench,
};

const GeneralTab: React.FC<GeneralTabProps> = ({ node, handleUpdate, allNodes, getScheduleName }) => {
    const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);
    const [isPhaseOutModalOpen, setIsPhaseOutModalOpen] = useState(false);
    const { groups, addGroup, updateGroup } = useScheduleGroups();
    const [groupOpen, setGroupOpen] = useState(false);
    const [groupFilter, setGroupFilter] = useState('');
    const groupRef = useRef<HTMLDivElement>(null);
    const [editingGroup, setEditingGroup] = useState<string | null>(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (groupRef.current && !groupRef.current.contains(e.target as Node)) {
                setGroupOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filteredGroups = useMemo(
        () => groups.filter(g => g.name.toLowerCase().includes(groupFilter.toLowerCase())),
        [groups, groupFilter]
    );

    const handleGroupSelect = (name: string) => {
        handleUpdate('group', name);
        setGroupFilter('');
        setGroupOpen(false);
    };

    const handleGroupInputChange = (value: string) => {
        setGroupFilter(value);
        handleUpdate('group', value);
        if (!groupOpen) setGroupOpen(true);
    };

    const handleGroupBlur = () => {
        // Auto-promote custom group on blur
        if (node.group && !groups.some(g => g.name === node.group)) {
            addGroup(node.group);
        }
    };

    // Follow Up Handlers
    const handleToggleFollowUp = (enabled: boolean) => {
        if (enabled) {
            handleUpdate('followUp', ''); // Enable with empty ID
        } else {
            handleUpdate('followUp', null); // Disable
        }
    };

    // Phase Out Handlers
    const handlePhaseOutChange = (field: 'scheduleId' | 'duration', value: string) => {
        if (!node.phaseOut && value) {
            handleUpdate('phaseOut', { scheduleId: value, duration: '30d' }); 
        } else if (node.phaseOut) {
            handleUpdate('phaseOut', { ...node.phaseOut, [field]: value });
        }
    };

    const handleTogglePhaseOut = (enabled: boolean) => {
        if (enabled) {
            handleUpdate('phaseOut', { scheduleId: '', duration: '30d' }); // Initialize empty
        } else {
            handleUpdate('phaseOut', null);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {/* Status */}
            <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${node.isActive ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-500'}`}>
                        {node.isActive ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                    </div>
                    <div>
                        <div className="font-semibold text-sm text-slate-900">Schedule Status</div>
                        <div className="text-xs text-slate-500">{node.isActive ? 'Active & Enforced' : 'Inactive & Ignored'}</div>
                    </div>
                </div>
                <label aria-label="Toggle schedule status" className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" checked={node.isActive} onChange={(e) => handleUpdate('isActive', e.target.checked)} />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                </label>
            </div>

            {/* Basic Info */}
            <div className="space-y-4">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2">Basic Information</h3>
                
                <div>
                    <label htmlFor="gen-name" className="text-xs font-semibold text-slate-500 mb-1 block">Name</label>
                    <input
                        id="gen-name"
                        type="text"
                        value={node.name} 
                        onChange={(e) => handleUpdate('name', e.target.value)} 
                        className="w-full p-2 bg-white text-slate-900 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div ref={groupRef} className="relative">
                        <label htmlFor="gen-group" className="text-xs font-semibold text-slate-500 mb-1 block">Group</label>
                        <div className="relative">
                            <input
                                id="gen-group"
                                type="text"
                                value={groupOpen ? groupFilter || node.group : node.group}
                                onChange={(e) => handleGroupInputChange(e.target.value)}
                                onFocus={() => { setGroupOpen(true); setGroupFilter(''); }}
                                onBlur={handleGroupBlur}
                                className="w-full p-2 pr-8 bg-white text-slate-900 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500"
                                placeholder="Select or type..."
                            />
                            <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => setGroupOpen(!groupOpen)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                                <ChevronDown size={14} />
                            </button>
                        </div>
                        {groupOpen && (
                            <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                {filteredGroups.map(g => {
                                    const Icon = g.icon ? ICON_MAP[g.icon] : undefined;
                                    const dotClass = DOT_COLORS[g.color || 'slate'] || DOT_COLORS.slate;
                                    return (
                                        <div
                                            key={g.name}
                                            className={`flex items-center hover:bg-slate-50 transition-colors ${
                                                node.group === g.name ? 'bg-teal-50 text-teal-700' : 'text-slate-700'
                                            }`}
                                        >
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); handleGroupSelect(g.name); }}
                                                className="flex-1 flex items-center gap-2 px-3 py-2 text-sm text-left"
                                            >
                                                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotClass}`} />
                                                {Icon && <Icon size={13} className="text-slate-400 flex-shrink-0" />}
                                                <span>{g.name}</span>
                                            </button>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setEditingGroup(g.name); setGroupOpen(false); }}
                                                className="px-2 py-2 text-slate-300 hover:text-slate-600 transition-colors"
                                                title={`Edit ${g.name} group`}
                                            >
                                                <Settings size={12} />
                                            </button>
                                        </div>
                                    );
                                })}
                                {filteredGroups.length === 0 && groupFilter && (
                                    <div className="px-3 py-2 text-xs text-slate-400 italic">
                                        Press Tab to add &quot;{groupFilter}&quot; as custom group
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div>
                        <label htmlFor="gen-purpose" className="text-xs font-semibold text-slate-500 mb-1 block">Purpose</label>
                        <input
                            id="gen-purpose"
                            type="text"
                            value={node.purpose} 
                            onChange={(e) => handleUpdate('purpose', e.target.value)} 
                            className="w-full p-2 bg-white text-slate-900 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500"
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="gen-tags" className="text-xs font-semibold text-slate-500 mb-1 block">Tags <span className="text-[10px] font-normal text-slate-400 opacity-80">(Search)</span></label>
                    <TagInput
                        id="gen-tags"
                        tags={node.tags || []}
                        onChange={(tags) => handleUpdate('tags', tags)}
                    />
                </div>

                <div>
                    <label htmlFor="gen-attributes" className="text-xs font-semibold text-slate-500 mb-1 block">Attributes <span className="text-[10px] font-normal text-slate-400 opacity-80">(Workflow Logic)</span></label>
                    <TagInput
                        id="gen-attributes"
                        tags={node.attributes || []}
                        onChange={(attrs) => handleUpdate('attributes', attrs)}
                        placeholder="Add attribute..."
                        colorClass="bg-indigo-50 text-indigo-700 border-indigo-200"
                    />
                </div>
            </div>

            {/* Lifecycle */}
            <div className="space-y-4">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2">Lifecycle</h3>
                
                {/* Follow Up */}
                <div className={`p-3 border rounded-lg transition-colors ${node.followUp !== null ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                            <Share2 size={16} className={node.followUp !== null ? "text-indigo-500" : "text-slate-400"} />
                            <div>
                                <div className="text-sm font-medium text-slate-900">Follow Up Schedule</div>
                            </div>
                        </div>
                        <label aria-label="Toggle follow up schedule" className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={node.followUp !== null}
                                onChange={(e) => handleToggleFollowUp(e.target.checked)} 
                            />
                            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-500"></div>
                        </label>
                    </div>
                    {node.followUp !== null && (
                            <div className="mt-3 space-y-3 pl-2 border-l-2 border-indigo-200">
                            <div>
                                <span className="text-xs text-slate-500 mb-1 block">Target Schedule</span>
                                <div className="flex gap-2">
                                    <div className="flex-1 text-sm p-2 bg-white border border-indigo-200 rounded text-slate-700 flex items-center justify-between">
                                            <span className={!node.followUp ? "text-slate-400 italic" : ""}>
                                            {node.followUp ? getScheduleName(node.followUp) : 'None configured'}
                                            </span>
                                    </div>
                                    <button 
                                        onClick={() => setIsFollowUpModalOpen(true)}
                                        className="px-3 py-1 bg-white border border-indigo-200 text-indigo-700 text-xs rounded hover:bg-indigo-100 transition-colors"
                                    >
                                        Select
                                    </button>
                                    {node.followUp && (
                                        <button 
                                            onClick={() => handleUpdate('followUp', '')}
                                            className="p-2 bg-white border border-indigo-200 text-indigo-700 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                                            title="Remove Follow Up"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Phase Out */}
                <div className={`p-3 border rounded-lg transition-colors ${node.phaseOut ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                            <Timer size={16} className={node.phaseOut ? "text-amber-500" : "text-slate-400"} />
                            <div>
                                <div className="text-sm font-medium text-slate-900">Phase Out Logic</div>
                            </div>
                        </div>
                        <label aria-label="Toggle phase out logic" className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={!!node.phaseOut}
                                onChange={(e) => handleTogglePhaseOut(e.target.checked)} 
                            />
                            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                        </label>
                    </div>
                    {node.phaseOut && (
                        <div className="mt-3 space-y-3 pl-2 border-l-2 border-amber-200">
                            <div>
                                <span className="text-xs text-slate-500 mb-1 block">Replacement Schedule</span>
                                <div className="flex gap-2">
                                    <div className="flex-1 text-sm p-2 bg-white border border-amber-200 rounded text-slate-700 flex items-center justify-between">
                                        <span className={!node.phaseOut.scheduleId ? "text-slate-400 italic" : ""}>
                                            {getScheduleName(node.phaseOut.scheduleId) || 'None configured'}
                                        </span>
                                    </div>
                                    <button 
                                        onClick={() => setIsPhaseOutModalOpen(true)}
                                        className="px-3 py-1 bg-white border border-amber-200 text-amber-700 text-xs rounded hover:bg-amber-100 transition-colors"
                                    >
                                        Select
                                    </button>
                                    {node.phaseOut.scheduleId && (
                                            <button 
                                            onClick={() => handlePhaseOutChange('scheduleId', '')}
                                            className="p-2 bg-white border border-amber-200 text-amber-700 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                                            title="Remove Replacement"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label htmlFor="gen-phaseout-duration" className="text-xs text-slate-500 mb-1 block">Duration</label>
                                <input
                                    id="gen-phaseout-duration"
                                    type="text"
                                    value={node.phaseOut.duration}
                                    onChange={(e) => handlePhaseOutChange('duration', e.target.value)}
                                    className="w-full p-2 bg-white text-slate-900 border border-amber-200 rounded text-sm focus:outline-none focus:border-amber-400"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ScheduleSelectionModal
                isOpen={isFollowUpModalOpen}
                onClose={() => setIsFollowUpModalOpen(false)}
                schedules={allNodes}
                onSelect={(id) => handleUpdate('followUp', id)}
                title="Select Follow Up Schedule"
                excludeId={node.scheduleId}
            />

            <ScheduleSelectionModal
                isOpen={isPhaseOutModalOpen}
                onClose={() => setIsPhaseOutModalOpen(false)}
                schedules={allNodes}
                onSelect={(id) => handlePhaseOutChange('scheduleId', id)}
                title="Select Replacement Schedule"
                excludeId={node.scheduleId}
            />

            {/* Group Edit Modal */}
            {editingGroup && (() => {
                const groupDef = groups.find(g => g.name === editingGroup);
                if (!groupDef) return null;
                return (
                    <div role="presentation" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30" onMouseDown={() => setEditingGroup(null)}>
                        <div role="dialog" aria-label={`Edit Group: ${groupDef.name}`} className="bg-white rounded-xl shadow-xl w-80 p-5" onMouseDown={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-bold text-slate-900">Edit Group: {groupDef.name}</h3>
                                <button onClick={() => setEditingGroup(null)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <span className="text-xs font-semibold text-slate-500 mb-2 block">Color</span>
                                    <div className="flex flex-wrap gap-2">
                                        {COLOR_OPTIONS.map(c => (
                                            <button
                                                key={c}
                                                type="button"
                                                onClick={() => updateGroup(groupDef.name, { color: c })}
                                                className={`w-7 h-7 rounded-full border-2 transition-all ${DOT_COLORS[c]} ${
                                                    (groupDef.color || 'slate') === c ? 'border-slate-800 scale-110' : 'border-transparent hover:border-slate-300'
                                                }`}
                                                title={c}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <span className="text-xs font-semibold text-slate-500 mb-2 block">Icon</span>
                                    <div className="flex flex-wrap gap-1.5">
                                        {ICON_OPTIONS.map(opt => {
                                            const IconComp = opt.name ? ICON_MAP[opt.name] : undefined;
                                            const isSelected = (groupDef.icon || '') === opt.name;
                                            return (
                                                <button
                                                    key={opt.name}
                                                    type="button"
                                                    onClick={() => updateGroup(groupDef.name, { icon: opt.name || undefined })}
                                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-all ${
                                                        isSelected ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                                                    }`}
                                                >
                                                    {IconComp ? <IconComp size={13} /> : <span className="text-slate-400">--</span>}
                                                    <span>{opt.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => setEditingGroup(null)}
                                className="mt-4 w-full py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default GeneralTab;